<template>
  <Filters
    v-model:filtersValues="filtersValues"
    :publicationState
    :resetFilters
    :submitFilters
  />

  <RevCard v-if="isFetchingListings">
    <RevLoadingScreen
      class="py-72"
      :text="i18n(translations.loading)"
      variant="light"
    />
  </RevCard>

  <div v-else>
    <Ordering
      :active-publication-state="publicationState"
      class="mb-36"
      :count="listingsCount"
      :listings-per-page="filtersValues.pageSize"
      :reference-market="selectedReferenceMarket"
      :sort-by="filtersValues.orderBy"
      @listing-display-change="handleListingDisplayChange"
      @reference-market-change="handleReferenceMarketChange"
      @update:listings-per-page="setListingsPerPage"
      @update:sort-by="handleSortByChange"
    />

    <div v-if="listings.length" class="space-y-24">
      <Listing
        v-for="listing in listings"
        :key="listing.id"
        :has-backpricer-enabled="hasBackpricerEnabled || false"
        :listing
        :publication-state
        :reference-market="selectedReferenceMarket"
        :should-expand="shouldExpandListing"
        @refresh-listings="fetchListings"
      />

      <Pagination
        class="py-56"
        :current-page="Number(filtersValues.page)"
        :page-count
        @click="({ page }) => setCurrentPage(page)"
      />
    </div>

    <div v-else>
      <EmptyState :publication-state />
    </div>

    <ListingDetailsDrawer />
  </div>
</template>

<script setup lang="ts">
import { useFilters, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { MarketCountryCode, merchantsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import {
  PUBLICATION_STATE,
  REFERENCE_MARKET_STORAGE_KEY,
  STORAGE_VIEW_TYPE_NAME,
} from '~/scopes/listings/Listings.constants'

import { useListings } from '../../composables/useListings'
import EmptyState from '../EmptyState/EmptyState.vue'
import { INITIAL_FILTERS_VALUES } from '../Filters/Filters.constants'
import Filters from '../Filters/Filters.vue'
import Listing from '../Listing/Listing.vue'
import ListingDetailsDrawer from '../ListingDetailsDrawer/ListingDetailsDrawer.vue'
import { VIEWS } from '../Ordering/Ordering.constants'
import Ordering from '../Ordering/Ordering.vue'

import translations from './Tab.translations'

const route = useRoute()
const i18n = useI18n()
const { activeMarkets, hasOnlyOneMarket } = useSellerInfoStore()
const localStorage = useLocalStorage()
const tracking = useTracking()
const publicationState = ref(PUBLICATION_STATE.ONLINE)
// Use activeMarkets.length instead of hasOnlyOneMarket from store
// because UK sellers (previously in EU) have non-empty potentialMarkets.
// And since the hasOnlyOneMarket rely on the potentialMarkets + activeMarkets length info,
// it causes hasOnlyOneMarket to return false incorrectly in the UK case.
const shouldExpandListing = ref(
  activeMarkets.length <= 1 ||
    localStorage.getItem(STORAGE_VIEW_TYPE_NAME) === VIEWS.DETAILED,
)

const selectedReferenceMarket = ref<MarketCountryCode | ''>(
  hasOnlyOneMarket
    ? activeMarkets[0]
    : (localStorage.getItem(REFERENCE_MARKET_STORAGE_KEY) as
        | MarketCountryCode
        | '') || '',
)

const {
  filtersValues,
  sanitizedFiltersValues,
  submitFilters,
  resetFilters,
  setCurrentPage,
} = useFilters({
  ...INITIAL_FILTERS_VALUES,
})

const {
  listings,
  listingsCount,
  getListings,
  isFetchingListings,
  formatListingToTrackingEvent,
} = useListings()

const { data: hasBackpricerEnabled } = await useHttpFetch(
  merchantsAPI.getBackPricerStatus,
  {
    transform: (data) => data?.backpricerEnabled,
  },
)

const queryParams = computed(() => ({
  ...sanitizedFiltersValues.value,
}))

const pageCount = computed(() =>
  listingsCount.value
    ? Math.ceil(
        listingsCount.value / parseInt(filtersValues.value.pageSize, 10),
      )
    : 1,
)

async function setListingsPerPage(listingsPerPage: string) {
  filtersValues.value.pageSize = listingsPerPage
  await submitFilters()
}

function handleListingDisplayChange(value: string) {
  shouldExpandListing.value = value === VIEWS.DETAILED
  localStorage.setItem(
    STORAGE_VIEW_TYPE_NAME,
    value === VIEWS.DETAILED ? VIEWS.DETAILED : VIEWS.SIMPLE,
  )

  tracking.trackClick({
    page_type: 'listings',
    zone: 'sort_display',
    name: 'display_type',
    value,
  })

  if (value === VIEWS.DETAILED) {
    listings.value.forEach((listing) => {
      tracking.trackBoSellerListingImpression({
        page_type: 'listings',
        zone: 'listing_details',
        name: 'listing_impression',
        ...formatListingToTrackingEvent(listing),
      })
    })
  }
}

function handleReferenceMarketChange(value: string) {
  selectedReferenceMarket.value = value as MarketCountryCode

  localStorage.setItem(
    REFERENCE_MARKET_STORAGE_KEY,
    selectedReferenceMarket.value,
  )

  tracking.trackClick({
    page_type: 'listings',
    zone: 'sort_display',
    name: 'main_market',
    market: selectedReferenceMarket.value,
  })
}

async function handleSortByChange(value: string) {
  filtersValues.value.orderBy = value
  await submitFilters()
}

async function fetchListings() {
  const formatedPublicationState =
    publicationState.value === PUBLICATION_STATE.ONLINE
      ? [PUBLICATION_STATE.ONLINE, PUBLICATION_STATE.OFFLINE]
      : publicationState.value

  await getListings({
    ...queryParams.value,
    publicationState: formatedPublicationState,
  })
}

watch(
  () => queryParams.value,
  async () => {
    if (
      Object.values(PUBLICATION_STATE).includes(
        route.meta?.publicationState as string,
      )
    ) {
      publicationState.value = route.meta?.publicationState as string
    }

    await fetchListings()
    if (shouldExpandListing.value) {
      listings.value.forEach((listing) => {
        tracking.trackBoSellerListingImpression({
          page_type: 'listings',
          zone: 'listing_details',
          name: 'listing_impression',
          ...formatListingToTrackingEvent(listing),
        })
      })
    }
  },
  { immediate: true },
)
</script>
